import React, { useEffect } from 'react';
import { useGoodsDiscounts } from '../../../queries/useGoodsDiscounts';
import Animated, {
  Easing,
  interpolate,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Colors, Flex, IconTimer, Typography } from '@banhala/ui-kit';
import type { GoodsDiscountGroupItem } from 'shared/src/api/goodsService';
import { getRemainTime, isNotNil } from 'shared/src/utils';
import { DiscountItemView } from './DiscountItemView';
import { AblyPayItemView } from './AblyPayItemView';
import { useAblyPayBenefitDiscount } from '../hooks/useAblyPayBenefitDiscount';

export const DiscountInfo: React.FC<{
  expanded: boolean;
  checked: GoodsDiscountGroupItem[];
  onChangeChecked: (checked: GoodsDiscountGroupItem[]) => void;
  salePrice: number;
}> = ({ expanded, checked, onChangeChecked, salePrice }) => {
  const { data: goodsDiscounts } = useGoodsDiscounts();
  const measuredHeight = useSharedValue(0);
  const heightAnim = useSharedValue<number | undefined>(undefined);
  const isExpanded = useSharedValue(expanded);

  useEffect(() => {
    isExpanded.value = expanded;
  }, [expanded, isExpanded]);

  // UI 스레드에서 애니메이션 처리 (setState 없이)
  useAnimatedReaction(
    () => ({ expanded: isExpanded.value, height: measuredHeight.value }),
    ({ expanded: exp, height }) => {
      if (height > 0) {
        heightAnim.value = withTiming(exp ? height : 0, {
          duration: 300,
          easing: Easing.bezier(0.4, 0.0, 0.2, 1.0), // FastOutSlowInEasing (Android 동일)
        });
      }
    },
    [],
  );

  const styles = useAnimatedStyle(() => {
    return {
      height: heightAnim.value,
      paddingHorizontal: 16,
      borderColor: Colors.border_tertiary,
      overflow: 'hidden',
      borderRadius: 8,
      borderWidth: interpolate(
        heightAnim.value ?? 0,
        [0, 1, measuredHeight.value],
        [0, 1, 1],
      ),
    };
  });

  const { ablyPayFirstOrderBenefitItem, discountAmount } =
    useAblyPayBenefitDiscount(salePrice, checked);

  return (
    <Animated.View
      style={styles}
      onLayout={({ nativeEvent }) => {
        const newHeight = nativeEvent.layout.height;
        if (newHeight > measuredHeight.value) {
          measuredHeight.value = newHeight;
        }
      }}
    >
      <Flex padding={{ top: 16, bottom: 12 }} gap={16}>
        {goodsDiscounts?.discount_groups.map(group => {
          return (
            <Flex gap={4} key={group.title}>
              <Flex.Row alignItems={'center'}>
                <Typography type={'subtitle4'} color={'content_tertiary'}>
                  {group.title}
                </Typography>
                {isNotNil(group.finished_at) &&
                  isNotNil(getRemainTime(group.finished_at)) && (
                    <Flex.Row
                      backgroundColor={'pink5'}
                      padding={{ horizontal: 4, vertical: 0 }}
                      borderRadius={'xsmall'}
                      alignItems={'center'}
                      justifyContent={'center'}
                    >
                      <IconTimer size={12} fill={Colors.content_pink_primary} />
                      <Typography
                        type={'subtitle6'}
                        color={'content_pink_primary'}
                      >
                        {getRemainTime(group.finished_at)} 남음
                      </Typography>
                    </Flex.Row>
                  )}
              </Flex.Row>
              {group.items.map(item => (
                <DiscountItemView
                  key={`${group.title}-${item.key}`}
                  item={item}
                  group={group}
                  checked={checked}
                  onChangeChecked={onChangeChecked}
                />
              ))}
            </Flex>
          );
        })}
        {isNotNil(ablyPayFirstOrderBenefitItem) && isNotNil(discountAmount) && (
          <Flex gap={4}>
            <Flex.Row alignItems={'center'}>
              <Typography type={'subtitle4'} color={'content_tertiary'}>
                포인트
              </Typography>
            </Flex.Row>
            {/* amount를 찾아야하는데.. */}

            <AblyPayItemView
              item={{
                title: '에이블리 페이',
                key: 'ABLY_PAY',
                amount: discountAmount,
                logging: {
                  analytics: {},
                },
              }}
              checked={checked}
              onChangeChecked={onChangeChecked}
              salePrice={salePrice}
            />
          </Flex>
        )}
      </Flex>
    </Animated.View>
  );
};
